import {
  Alert,
  Col,
  Collapse,
  Divider,
  Form,
  Icon,
  message,
  Modal,
  Row,
  Spin,
  Tooltip
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import {
  approveTask,
  deleteTaskDocuments,
  getTaskDetails,
  rejectTask,
  updateTaskDetails,
  updateTaskDocuments,
} from 'src/api/shipmentExecution';
import CrainIcon from 'src/components/common/icons/CrainIcon';
import SEAwb from 'src/components/common/icons/SEAwb';
import ApiDto from 'src/dto/ApiDto';
import { SETask } from 'src/dto/response/fetchShipmentTasks';
import useFieldGroup from 'src/hooks/form/useFieldGroup';
import useApi from 'src/hooks/useApi';
import useExpandable from 'src/hooks/useExpandable';
import { useReduxState } from 'src/hooks/useRedux';
import useTabs from 'src/hooks/useTabActivation';
import useVisibility from 'src/hooks/useVisibility';
import { isValidArray } from 'src/Library/Helper';
import { StylesProps } from 'src/Library/Types';
import {
  getQuoteTotals,
  isPerVendorQuoteField,
  isQuoteCandidatesField,
  isQuoteSelectionTaskFields,
  QuoteTotals,
  QUOTE_DEVIATION_REMARKS_CODE,
  QUOTE_L1_DEVIATION_REASON_CODE,
  QUOTE_TOTAL_AMOUNT_CODE,
  QUOTE_TOTAL_L1_DEVIATION_CODE,
  taskResourceTypes,
} from '../../../SE.constants';
import { DocumentDetailsDto, QuoteType } from '../../../SE.interfaces';
import { SEQuoteCandidatesContext } from '../../../SECommon/hooks/SEQuotesComponents/SEQuoteSelectionContext';
import SEDeviationReasonModal, { DeviationInfo } from './SEDeviationReasonModal';
import SEContainer from '../../../SECommon/SEContainer';
import SEFieldGroup from '../../../SECommon/SEFieldGroup';
import { parseFormValues } from '../../../SECommon/SEFieldGroupUtils';
import { Document } from '../../SETasks/SETasks.interfaces';
import useModel from './SEAddTaskDetailModal.model';
import styles from './SEAddTaskDetailModal.styles';
import SEDocumentBar from './SEDocumentBar';
import SETaskRejectionRemarkModal from './SETaskRejectionRemarkModal';
import SEUploadDocumentsTaskDetails from './SEUploadDocumentsTaskDetails';

interface SEAddTaskDetailModalProps
  extends FormComponentProps<any>,
    StylesProps<ReturnType<typeof styles>> {
  handleClose: (refresh?: boolean) => Promise<void>;
  editable: boolean;
  sourceObjectId: string;
  taskId: string;
  taskName: string;
  onGotoTask: (taskId: string, formData?: any, docEditValues?: any) => void;
  taskData?: SETask;
  isTaskApproval?: boolean;
  enableApproveReject: boolean;
  sessionData?: any;
  markDone: boolean;
  taskUpdateActions: 'UPDATE' | 'REJECT' | 'APPROVE' | undefined;
  milestone: string;
  setTaskUpdateActions: (action: 'UPDATE' | 'REJECT' | 'APPROVE' | undefined) => void;
  setIsUpdateLoading: (isLoading: boolean) => void;
  setIsApproveLoading: (isLoading: boolean) => void;
  setIsRejectLoading: (isLoading: boolean) => void;
  setIsUpdateDisabled: (isUpdateDisabled: boolean) => void;
  gotoContainerTrackingTab: () => void;
  isRejectLoading: boolean;
  docEditTaskSessionData?: any;
  enableMultipleDuplicateValues?: boolean;
  enableGridView?: boolean;
  mode?: string;
}

const SEAddTaskDetailModal: React.FC<SEAddTaskDetailModalProps> = ({
  form,
  classes,
  handleClose,
  taskId,
  taskName,
  sourceObjectId,
  editable,
  onGotoTask,
  taskData,
  sessionData,
  /** from SEModalWrapper */
  markDone,
  taskUpdateActions,
  milestone,
  setTaskUpdateActions,
  setIsUpdateLoading,
  setIsApproveLoading,
  setIsRejectLoading,
  setIsUpdateDisabled,
  gotoContainerTrackingTab,
  isRejectLoading,
  docEditTaskSessionData,
  enableMultipleDuplicateValues,
  enableGridView,
  mode,
}) => {
  const initialState = {};
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const [isConfirmationModalLoading, setIsConfirmationModalLoading] = useState(false);
  const [documentToBeDeleted, setDocumentToBeDeleted] = useState<{
    attachmentUrl?: string;
    documentId?: string;
    documentCount?: number;
  }>({});

  const [reasonToggleMap, setReasonToggleMap] = useState({} as { [id: string]: boolean });
  const [editedDocumentList, setEditedDocumentList] = useState({} as {
    [documentId: string]: {
      name: string;
      s3Link: string;
      uid: string;
    }[]
  })

  const fieldsConfig = useModel({
    form,
    id: sourceObjectId,
    taskId,
    defaultDisabled: !editable,
    onGotoTask,
  });
  const taskFields = useFieldGroup({
    defaultNodes: [
      // ...manualNodes(),
    ],
    extraData: fieldsConfig.data?.taskDetails,
    // onFirstRender: async () => form.setFieldsValue(preValues),
  });
  const parentTaskFields = useFieldGroup({
    defaultNodes: [
      // ...manualNodes(),
    ],
    // onFirstRender: async () => form.setFieldsValue(preValues),
  });
  const editTaskApi = useApi(updateTaskDetails);
  const approveTaskApi = useApi(approveTask);
  const rejectTaskApi = useApi(rejectTask);
  const expandable = useExpandable(200);
  const modals = useVisibility(['rejectionRemark']);
  const dropdownMasterValues = useReduxState(['shipmentExecutionReducer', 'dropdownMaster']).value;

  const {
    taskDetails,
    reloadModal,
    parentTaskMeta,
    parentTaskDetails,
    getNestedFieldValues,
  } = fieldsConfig;
  const tabs = useTabs(4);

  const onHold = milestone === 'ON_HOLD' || milestone === 'DELETED';

  /**
   * SMT71 (Quote Selection): the task carries per-vendor RFQ_QUOTE fields (rendered as tabs) plus
   * summary fields (Total Quote Amount, Total L1 Deviation, L1 Deviation Reason, Deviation Remarks)
   * that are NOT rendered inline. Their values are computed from the selected quotes / captured in the
   * deviation popup and injected into the submit payload below.
   */
  const rawTaskFields = fieldsConfig.data?.taskDetails?.taskFields;
  const isQuoteTask = isQuoteSelectionTaskFields(rawTaskFields);
  // Match on masterCode first (the reliable identifier), falling back to code — the backend does not
  // always populate `code`, mirroring isQuoteCandidatesField / getVendorTypeFromQuoteField.
  const findQuoteFieldByCode = (code: string) =>
    rawTaskFields?.find((field) => (field.masterCode || field.code) === code);
  const deviationInfoRef = useRef<DeviationInfo | null>(null);
  const [deviationModal, setDeviationModal] = useState<{
    visible: boolean;
    totalQuoteAmount: number;
    totalDeviation: number;
  }>({ visible: false, totalQuoteAmount: 0, totalDeviation: 0 });

  /**
   * Quote Candidates (SMF427): each per-vendor panel reports its full displayed quote list here (keyed
   * by vendor type). On submit we merge them into one list and write it to SMF427 so the L1-deviation
   * approver (SMT72) sees the exact snapshot the ops user chose from. Held in a ref — pure capture that
   * must not trigger re-renders — mirroring deviationInfoRef.
   */
  const quoteCandidatesRef = useRef<Record<string, QuoteType[]>>({});
  const reportCandidates = useCallback((fieldKey: string, quotes: QuoteType[]) => {
    quoteCandidatesRef.current[fieldKey] = quotes;
  }, []);

  /**
   * The frozen SMF427 snapshot as saved at SMT71, read straight off the raw payload (SMF427 is not
   * rendered inline). Passed to the read-only panels so the approval view (SMT72) can show every quote
   * that was available at selection time, grouped by vendor type.
   */
  const savedQuoteCandidates = React.useMemo(() => {
    const candidatesField = rawTaskFields?.find(isQuoteCandidatesField);
    return (candidatesField?.values as unknown as QuoteType[]) || [];
  }, [rawTaskFields]);

  // Stable context value so consumers don't re-render on this component's unrelated re-renders.
  // Both deps are already stable (reportCandidates is a useCallback, savedQuoteCandidates a useMemo).
  const quoteCandidatesContextValue = React.useMemo(() => ({
    reportCandidates,
    savedCandidates: savedQuoteCandidates,
  }), [reportCandidates, savedQuoteCandidates]);

  /**
   * Totals are computed from the quote selected on each RFQ_QUOTE field. SEQuotePanel stores the chosen
   * QuoteType at `taskFields[<fieldId>]`, so we read the form directly (getFieldValue does not trigger
   * validation).
   */
  const computeQuoteTotals = (): QuoteTotals => {
    // Form values are keyed by fieldId; SEQuotePanel stores the chosen QuoteType at each per-vendor
    // quote field's key, which is all this reads.
    const values = (form.getFieldValue('taskFields') || {}) as Record<string, QuoteType | undefined>;
    const selectedQuotes = (rawTaskFields?.filter(isPerVendorQuoteField) || [])
      .map((field) => values[field.fieldId])
      .filter((quote): quote is QuoteType => Boolean(quote));
    return getQuoteTotals(selectedQuotes);
  };

  /** Non-zero to 2 decimals — matches the footer/modal display and avoids float noise (0.1 + 0.2). */
  const isDeviationNonZero = (totalDeviation: number) => Math.round(totalDeviation * 100) !== 0;

  const handleDeviationConfirm = (info: DeviationInfo) => {
    deviationInfoRef.current = info;
    setDeviationModal((prev) => ({ ...prev, visible: false }));
    handleSubmit(true);
  };

  const handleDeviationCancel = () => {
    setDeviationModal((prev) => ({ ...prev, visible: false }));
  };

  const handleAddDocumentDetail = async () => {
    const response = await getTaskDetails({
      id: sourceObjectId,
      taskId,
    });
    if (response.isSuccess) {
      const payloadObj = handleAddDetail();
      const params = {
        fields: response.data?.taskDetails.taskFields,
        containers: response.data?.taskDetails.containers,
        documents: response.data?.taskDetails.documents,
        awbs: response.data?.taskDetails.awbs,
        markDone,
        markInProgress: !markDone,
      } as any;

      if (payloadObj?.id && payloadObj?.params?.fields?.length) {
        params.fields = payloadObj?.params?.fields;
      };

      const apiCallObject = {
        id: sourceObjectId,
        taskId,
        params,
      };
      
      editTaskApi.call(apiCallObject);
    }
  };

  const handleSubmit = async (skipDeviationGate = false) => {

    /**
     * SMT71 deviation gate — only on final completion (markDone). When the selected quotes deviate
     * from L1, capture the reason + remarks in a popup BEFORE submitting. `handleSubmit(true)` from the
     * popup's confirm resumes past this gate; the document-upload effect always calls handleSubmit()
     * (no flag) so every fresh submit re-gates. Must stay above the document branch so the resume
     * runs the upload exactly once.
     */
    if (!skipDeviationGate && markDone && isQuoteTask) {
      const totals = computeQuoteTotals();
      if (isDeviationNonZero(totals.totalDeviation)) {
        setDeviationModal({ visible: true, ...totals });
        return;
      }
    }

  const hasDocumentData = documentApiCallObject && Object.keys(documentApiCallObject).length > 0;
    /**
     * If documentApiCallObject is empty {} or no documents to upload
     * make the API call directly
    * */
      if (!hasDocumentData) {
        const payload = handleAddDetail();
        if (payload) {
          handleSubmitAddEditDetails(payload)
        }
        return;
      }

    /**
     * If document is present
     */
    if (hasDocumentData) {
      setIsUpdateLoading(true);
      const documentUpdateResponse = await updateTaskDocuments(
        documentApiCallObject as DocumentDetailsDto
      );
      if (documentUpdateResponse.isSuccess) {
        message.success('Document added successfully!');
        handleAddDocumentDetail();
        return;
      }
      message.error(documentUpdateResponse.errorMessage || 'Something went wrong!');
      setIsUpdateLoading(false);
    }
  };

  const [documentApiCallObject, setDocumentApiCallObject] = useState<DocumentDetailsDto | {}>();

  useEffect(() => {
    if (!documentApiCallObject) {
      return;
    }
    if (documentApiCallObject) {
      handleSubmit();
    }
  }, [documentApiCallObject]);

  React.useEffect(() => {
    if (taskUpdateActions) {
      switch (taskUpdateActions) {
        case 'UPDATE': {
          // handleSubmit();
          /* *
            This is handled in SEUploadDocumentsTaskDetails since
            it is leading to redundant API calls when document upload is involved
          * */   
          break;
        }
        case 'REJECT': {
          handleShowRejectionModal();
          break;
        }
        case 'APPROVE': {
          handleApprove();
          break;
        }
      }
    }
    setTaskUpdateActions(undefined);
  }, [taskUpdateActions]);

  React.useEffect(() => {
    setTimeout(() => {
      if (sessionData) {
        form.setFieldsValue(sessionData);
      }
    }, 1000);
    if (docEditTaskSessionData) {
      setEditedDocumentList(docEditTaskSessionData)
    }
  }, [tabs.visiblePanes.length]);

  React.useEffect(() => {
    taskFields.replaceNodes([
      ...taskDetails?.taskNodes,
      ...taskDetails.containerNodes,
      ...taskDetails.awbNodes,
      ...taskDetails.docNodes,
    ]);
  }, [taskDetails.taskNodes, taskDetails.containerNodes, taskDetails.docNodes, taskDetails.awbNodes]);

  React.useEffect(() => {
    parentTaskFields.replaceNodes([
      ...parentTaskDetails?.taskNodes,
      ...parentTaskDetails.containerNodes,
      ...parentTaskDetails.awbNodes,
      ...parentTaskDetails.docNodes,
    ]);
  }, [parentTaskDetails.taskNodes, parentTaskDetails.containerNodes, parentTaskDetails.docNodes, parentTaskDetails.awbNodes]);

  React.useEffect(() => {
    tabs.hideAll();
    const tabsToShow = [] as string[];
    isValidArray(taskDetails.taskNodes) ||
    taskName?.includes('DOA Approval for AIR Mode level') ||
    taskName?.includes('EXIM Approval of ASN')
      ? tabsToShow.push('1')
      : null;
    fieldsConfig.data?.isContainerFieldsPresent ? tabsToShow.push('2') : null;
    isValidArray(taskDetails.docNodes) ? tabsToShow.push('3') : null;
    isValidArray(taskDetails.attachments) ? tabsToShow.push('4') : null;
    tabs.show(tabsToShow);
  }, [
    taskDetails.taskNodes,
    taskDetails.containerNodes,
    taskDetails.awbNodes,
    taskDetails.docNodes,
    taskDetails.attachments,
    taskName,
  ]);

  React.useEffect(() => {
    if (editTaskApi.status === 'RUNNING') {
      setIsUpdateLoading(true);
    }
    if (editTaskApi.status === 'FINISHED') {
      handleClose(true);
      message.success('Fields updated successfully!');
      setIsUpdateLoading(false);
      setEditedDocumentList({});
      // Consumed — clear so a subsequent submit re-gates. On FAILED we keep it to pre-fill the popup.
      deviationInfoRef.current = null;
    }
    if (editTaskApi.status === 'FAILED') {
      message.error(editTaskApi.errorMessage);
      setIsUpdateLoading(false);
      form.validateFields(async (err, values) => {
        onGotoTask(taskId, values);
      });
    }
  }, [editTaskApi.status]);

  React.useEffect(() => {
    if (approveTaskApi.status === 'RUNNING') {
      setIsApproveLoading(true);
      return;
    }
    if (approveTaskApi.status === 'FINISHED') {
      message.success('Task approved successfully!');
      handleClose(true);
    }
    if (approveTaskApi.status === 'FAILED') {
      message.error(approveTaskApi.errorMessage);
    }
    setIsApproveLoading(false);
  }, [approveTaskApi.status]);

  React.useEffect(() => {
    if (rejectTaskApi.status === 'RUNNING') {
      setIsRejectLoading(true);
      return;
    }
    if (rejectTaskApi.status === 'FINISHED') {
      message.success('Task rejected successfully!');
      handleClose(true);
    }
    if (rejectTaskApi.status === 'FAILED') {
      message.error(rejectTaskApi.errorMessage);
    }
    setIsRejectLoading(false);
  }, [rejectTaskApi.status]);

  const handleRejection = async ({
    rejectionReason,
    rejectionDocumentUrl,
  }: {
    rejectionReason: string;
    rejectionDocumentUrl: string;
  }) => {
    taskData?.id &&
      rejectTaskApi.call({
        id: sourceObjectId,
        taskId: taskData.id,
        rejectionReason,
        rejectionDocumentUrl,
      });
  };

  const getFieldsForApi = (fields: any[], enableMultipleDuplicateValues) => {
    if (!fields) return [];
    const fieldValues = fields
      ?.map((values, fieldId) => {
        return {
          fieldId: fieldId?.toString(),
          values: parseFormValues(values, enableMultipleDuplicateValues),
        };
      })
      .filter((el) => Array.isArray(el.values));
    const ret = getNestedFieldValues(fieldValues);
    return ret;
  };

  const handleAddDetail = () => {
    const documentIdMap = {};
    const existingDocuments = {};
    taskDetails.docNodes?.forEach((node) => {
      existingDocuments[node?.renderProps?.extraData?.documentId] = {
        fileName: node?.renderProps?.extraData?.fileName,
        url: node?.renderProps?.extraData?.url
      }
      node?.renderProps?.extraData?.fields?.forEach((field) => {
        documentIdMap[field?.fieldId] = node?.renderProps?.extraData?.documentId;
      });
    });
    let isError = false;
    let payload = {} as {
      id: string,
      taskId: string,
      params: typeof ApiDto.UpdateTaskDetailsForShipmentDto
    };
    form.validateFields((err, values) => {
      if (err && markDone) {
        // Check if there are any validation errors that are not related to parent task fields.
        const hasNonParentErrors = Object.keys(err).some(key => !key.startsWith('parent_'));
      
        
        // Only set error if there are non-parent errors or parent task is not 'Done'
        if (hasNonParentErrors || parentTaskMeta?.status !== 'Done') {
          isError = true;
          return;
        }
      
        // If parent task is 'Done', ignore parent field errors by clearing the error object.
        err = null;
      }
      let awbs: any[] = []; 
      if (values?.awbField?.[0]) {
        // Process each AWB entry's fields
        values.awbField.forEach((awbEntry: any) => {
          for (let [key, value] of Object.entries(awbEntry)) {
            let fieldId = key;
            if (moment.isMoment(value)) {
              value = value.valueOf();
            }
            awbs.push({ fieldId, values: value ? [value] : [] });
          }
        });
      }
      const fields = getFieldsForApi(values?.taskFields, enableMultipleDuplicateValues) || [];

      /**
       * SMT71 summary fields are not rendered inline, so inject their computed/captured values here:
       * Total Quote Amount + Total L1 Deviation always; L1 Deviation Reason + Remarks when the
       * deviation popup was confirmed. These fieldIds are never form-registered, so this cannot dupe.
       */
      if (isQuoteTask) {
        const totals = computeQuoteTotals();
        const pushQuoteFieldValue = (code: string, value: string | number | undefined) => {
          const targetField = findQuoteFieldByCode(code);
          if (targetField && value !== undefined && value !== null && value !== '') {
            fields.push({ fieldId: targetField.fieldId.toString(), values: [value] });
          }
        };
        pushQuoteFieldValue(QUOTE_TOTAL_AMOUNT_CODE, totals.totalQuoteAmount);
        pushQuoteFieldValue(QUOTE_TOTAL_L1_DEVIATION_CODE, totals.totalDeviation);
        /**
         * Only inject the captured reason/remarks when they are actually warranted: on final completion
         * (markDone) AND when the current selection still deviates. deviationInfoRef persists across a
         * failed submit retry, so gating on the ref alone would leak a stale justification onto a later
         * zero-deviation selection or an interim (non-markDone) save — matching the gate in handleSubmit.
         */
        if (markDone && isDeviationNonZero(totals.totalDeviation) && deviationInfoRef.current) {
          pushQuoteFieldValue(QUOTE_L1_DEVIATION_REASON_CODE, deviationInfoRef.current.reason);
          pushQuoteFieldValue(QUOTE_DEVIATION_REMARKS_CODE, deviationInfoRef.current.remarks);
        }

        /**
         * Quote Candidates (SMF427) — the full displayed list across all included vendor types, from
         * the same quote-search results the user selected from. MULTIPLE field, so `values` is the
         * whole array (not wrapped). The backend integrity check requires every selected quote's bidId
         * to be present here, which holds because these are the exact lists the selections came from.
         */
        const candidatesField = rawTaskFields?.find(isQuoteCandidatesField);
        if (candidatesField) {
          const allCandidates = Object.values(quoteCandidatesRef.current).reduce(
            (acc, list) => acc.concat(list || []),
            [] as QuoteType[],
          );
          fields.push({ fieldId: candidatesField.fieldId.toString(), values: allCandidates });
        }
      }

      const containers =
        values?.containerField
          ?.reduce((acc, grp) => {
            const res = getFieldsForApi(grp, enableMultipleDuplicateValues);
            acc.push(...res);
            return acc;
          }, [])
          .filter((el) => el) || [];
      const documents =
        values?.documentField
          ?.map((grp) => {
            const documentId = documentIdMap[getFieldsForApi(grp, enableMultipleDuplicateValues)[0]?.fieldId];
            const fields = getFieldsForApi(grp, enableMultipleDuplicateValues);
            let newDocFileNames = '';
            let newDocS3Links = '';
            if (Object.keys(editedDocumentList)?.length) {
              newDocFileNames = editedDocumentList[documentId]?.map((doc) => doc.name)?.join(',');
              newDocS3Links = editedDocumentList[documentId]?.map((doc) => doc.s3Link)?.join(',');
            }
            if (existingDocuments[documentId]?.url && newDocS3Links) {
              newDocFileNames = `${existingDocuments[documentId]?.fileName},${newDocFileNames}`;
              newDocS3Links = `${existingDocuments[documentId]?.url},${newDocS3Links}`;
            }
            const docPayload: any = {
              documentId,
              fields,
            }
            if (newDocFileNames && newDocS3Links) {
              docPayload.fileName = newDocFileNames;
              docPayload.url = newDocS3Links;
            }
            return docPayload;
          })
          .filter((el) => el) || [];
      const params: typeof ApiDto.UpdateTaskDetailsForShipmentDto = {
        fields,
        containers,
        awbs,
        documents,
        markDone,
        markInProgress: !markDone,
      };
      const apiCallObject = {
        id: sourceObjectId,
        taskId,
        params,
      };
      payload = { ...apiCallObject };
    });
    if (isError) {
      return;
    } else {
      return payload;
    }
  };

  const handleSubmitAddEditDetails = async (apiCallObject) => {
    editTaskApi.call(apiCallObject);
  }

  const handleShowRejectionModal = () => {
    modals.rejectionRemark.show();
  };

  const handleApprove = async () => {
    taskData?.id && approveTaskApi.call({ id: sourceObjectId, taskId: taskData.id });
  };

  const showDocumentDeleteConfirmationModal = () => {
    setIsConfirmationModalVisible(true);
  };

  const handleRemoveDocument = async () => {
    if (!taskData?.id) {
      return;
    }

    const { documentId, attachmentUrl } = documentToBeDeleted;
    if (!documentId || !attachmentUrl) {
      message.error('No Document Selected');
      return;
    }
    const newDocUrls = editedDocumentList?.[documentId]?.map(doc => doc.s3Link); 

    if (newDocUrls?.includes(attachmentUrl)) {
      const newDocList = editedDocumentList?.[documentId]?.filter(doc => doc.s3Link !== attachmentUrl);
      const editedDocList = editedDocumentList;
      editedDocList[documentId] = newDocList;
      setEditedDocumentList(editedDocList);
      form.validateFields((err, val) => {
        onGotoTask(taskData?.id, val, editedDocList)
      })
      return;
    }

    const response = await deleteTaskDocuments({
      documentId,
      attachmentUrl,
      id: sourceObjectId,
      taskId: taskData?.id,
    });

    if (response.isSuccess) {
      message.success('Document removed successfully.');
      form.validateFields((err, val) => {
        onGotoTask(taskData?.id, val)
      })
    } else {
      message.error(response.errorMessage);
    }

    setIsConfirmationModalLoading(false);
    setIsConfirmationModalVisible(false);
    setDocumentToBeDeleted({});
  };

  useEffect(() => {
    confirmDocumentDeletion();
  }, [documentToBeDeleted]);

  const confirmDocumentDeletion = async () => {
    /** Removing last document will also remove related fields */
    const { documentCount, documentId, attachmentUrl } = documentToBeDeleted;
    let isEditDocUrl = false;

    if (!documentCount) {
      return;
    }

    if (documentId && editedDocumentList[documentId]?.length) {
      isEditDocUrl = !!editedDocumentList[documentId]?.filter(doc => doc.s3Link === attachmentUrl)?.length;
    }

    if (documentCount === 1 && !isEditDocUrl) {
      showDocumentDeleteConfirmationModal();
      return;
    }

    await handleRemoveDocument();
  };

  const toggleReasonHeight = (id, isExpand = false) => {
    setReasonToggleMap({ ...reasonToggleMap, [id]: isExpand });
  };

  const renderSubgroups = (
    fields: typeof taskFields | typeof parentTaskFields,
    type: typeof taskResourceTypes[keyof typeof taskResourceTypes],
    dropdownMasterValues,
    isParentTask = false
  ) => {
    const renderRelatedFields = (subGroup) => {
      return (
        ((subGroup.extraData?.containerNumber && type === taskResourceTypes.CONTAINERS) ||
          subGroup.extraData?.awbNumber && type === taskResourceTypes.AWBS ||
          (subGroup.extraData?.documentType && type === taskResourceTypes.DOCUMENTS)) && (
          <SEFieldGroup
            fieldGroup={subGroup}
            style={{
              gridColumn: '1/3',
              display: 'grid',
              gridTemplateColumns: '1fr 3fr',
            }}
            dropdownMasterValues={dropdownMasterValues}
          />
        )
      );
    };

    const renderContainers = (subGroup) => {
      return (
        <div style={{ marginBottom: 20 }}>
          <SEContainer
            container={subGroup.extraData?.containerNumber}
            selected={true}
            nonClickable={true}
          />
        </div>
      );
    };

    const renderAwbs = (subGroup) => {
      return (
        <div style={{ marginBottom: 20 }}>
          <SEAwb
            awb={subGroup.extraData?.awbNumber}
            selected={true}
            nonClickable={true}
          />
        </div>
      );
    };

    function mergeDocuments(documents) {
      const merged = {};

      documents.forEach(doc => {
          const key = doc.extraData.documentTemplateId;
          const urlCount = doc.extraData.url ? doc.extraData.url.split(',').length : 0;
          const docIdsForRow = Array(urlCount).fill(doc.extraData.documentId);

          if (!merged[key]) {
              merged[key] = { ...doc, extraData: { ...doc.extraData } };
              merged[key].extraData.fileName = doc.extraData.fileName;
              merged[key].extraData.url = doc.extraData.url;
              merged[key].extraData.updatedAtTimestamps = doc.extraData.updatedAtTimestamps;
              merged[key].extraData.documentIds = docIdsForRow;
          } else {
              merged[key].extraData.fileName += `,${doc.extraData.fileName}`;
              merged[key].extraData.url += `,${doc.extraData.url}`;
              merged[key].extraData.updatedAtTimestamps += `#${doc.extraData.updatedAtTimestamps}`;
              merged[key].extraData.documentIds = merged[key].extraData.documentIds.concat(docIdsForRow);
          }
      });

      return Object.values(merged);
  }

    const renderDocumentList = (document: Document) => {
      if (!document) {
        return;
      }
      
      const { documentId } = document;
      const fileNames = document.fileName.split(',');
      const urls = document.url.split(',');
      const updatedAts = document.updatedAtTimestamps.split('#');
      const documentIds: string[] = (document as { documentIds?: string[] }).documentIds || urls.map(() => documentId);
      const documentCount = urls.length;

      if (editedDocumentList[documentId]?.length) {
        editedDocumentList[documentId]?.forEach((doc) => {
          urls.push(doc.s3Link);
          fileNames.push(doc.name);
        });
      }

      return urls.map((url, index) => {
        const isFirstElement = index === 0;

        const documentData = {
          ...document,
          fileName: fileNames[index],
          url,
          updatedAt: updatedAts[index],
        };

        return (

          <SEDocumentBar
            index={index}
            onRemove={() => {
              setDocumentToBeDeleted({
                attachmentUrl: url,
                documentId: documentIds[index] || documentId,
                documentCount,
              });
            }}
            isDeleteDisabled={!editable || isParentTask}
            document={documentData}
            showTitle={isFirstElement}
            showColumnHeader={isFirstElement}
            containerBoxStyle={{ margin: '10px 12px' }}
            form={form}
            setEditedDocumentList={setEditedDocumentList}
            editedDocumentList={editedDocumentList}
            setIsUpdateDisabled={setIsUpdateDisabled}
            editable={editable}
            isParentTask={isParentTask}
          />
        );
      });
    };
    const res = fields.render((elems) => {
      const subGroups: any[] = [];
      elems?.forEach((elem, idx) => {
        if (!elem) return;
        const { ref } = elem;
        const field = ref?.current?.field;

        if (field?.fieldType === 'group') {
          subGroups.push(field);
        }
      });

      const newSubGroups:any[] = type === taskResourceTypes.DOCUMENTS ? mergeDocuments(subGroups): subGroups;

      return newSubGroups.map((subGroup, idx) => {
        return (
          <div style={{ gridColumn: '1/3' }}>
            {subGroup.extraData?.containerNumber &&
              type === taskResourceTypes.CONTAINERS &&
              renderContainers(subGroup)}
            {subGroup.extraData?.awbNumber &&
              type === taskResourceTypes.AWBS &&
              renderAwbs(subGroup)}
            {subGroup.extraData?.documentType &&
              type === taskResourceTypes.DOCUMENTS &&
              renderDocumentList(subGroup.extraData)}
            {renderRelatedFields(subGroup)}
          </div>
        );
      });
    });

    return (
      <>
        {res}
        {!tabs.visiblePanes?.includes('1') &&
          renderTaskDetailFields(taskFields, taskDetails.dropdownMasterValues)}
        {/* Subgroups don't render without this line, if there was no task field. */}
      </>
    );
  };

  const renderTaskDetailFields = (
    fields: typeof taskFields | typeof parentTaskFields,
    dropdownMasterValues
  ) => {
    return (
      <SEFieldGroup
        duplicateField={taskDetails.duplicateField}
        deleteField={taskDetails.deleteField}
        fieldGroup={fields}
        style={{
          gridColumn: '1/3',
          display: 'grid',
          gridTemplateColumns: '1fr 3fr',
        }}
        dropdownMasterValues={dropdownMasterValues}
        enableGridView={enableGridView}
        shipmentMode={mode}
      />
    );
  };

  const renderAttachments = (Details: typeof taskDetails | typeof parentTaskDetails) => {
    return (
      <div>
        {Details.attachments?.map((doc, idx) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                marginRight: 10,
              }}
            >
              {idx + 1}.
            </span>
            <Icon type="paper-clip" />
            <a
              href={doc.url}
              download
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#1890FF',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {doc.fileName || doc.url}
            </a>
          </div>
        ))}
      </div>
    );
  };

  const renderTaskTabsv2 = () => {
    const noTracking = !(isValidArray(taskDetails.containerNodes) || isValidArray(taskDetails.awbNodes)) 
      && fieldsConfig.data?.isContainerFieldsPresent;
    if (fieldsConfig.status === 'FAILED') {
      return renderFailedAPI();
    }
    let type: typeof taskResourceTypes[keyof typeof taskResourceTypes];
    if (isValidArray(taskFields?.extraData?.containers)) {
      type = taskResourceTypes.CONTAINERS;
    } else if (isValidArray(taskFields?.extraData?.awbs)) {
      type = taskResourceTypes.AWBS;
    } else {
      type = taskResourceTypes.DOCUMENTS;
    }
    return (
      <div style={{ padding: '0px 10px' }}>
        {tabs.visiblePanes.includes('1') &&
          renderTaskDetailFields(taskFields, dropdownMasterValues)}
        {tabs.visiblePanes?.includes('2') && (noTracking
          ? renderNoContainers()
          : renderSubgroups(taskFields, type, dropdownMasterValues))}
        {tabs.visiblePanes?.includes('3') && 
          renderSubgroups(taskFields, taskResourceTypes.DOCUMENTS, dropdownMasterValues)}
        {tabs.visiblePanes?.includes('4') && renderAttachments(taskDetails)}
      </div>
    );
  };

  const renderSeeMore = () => {
    if (!expandable.expandable) {
      return null;
    }
    return expandable.collapsed ? (
      <div
        style={{
          position: 'absolute',
          top: 170,
          right: 18,
          fontWeight: 600,
          cursor: 'pointer',
          color: '#1478F6',
        }}
        onClick={expandable.expand}
      >
        See more
      </div>
    ) : (
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          right: 18,
          fontWeight: 600,
          cursor: 'pointer',
          color: '#1478F6',
        }}
        onClick={expandable.collapse}
      >
        See less
      </div>
    );
  };

  const renderParentTask = () => {
    return parentTaskMeta?.id
      ? expandable.decorator(
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: 15,
              backgroundColor: '#FFFDEA',
              marginBottom: 10,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {parentTaskMeta.status !== 'Done' ? (
              renderIncompleteParentTask()
            ) : (
              <>
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 20,
                  }}
                >
                  {parentTaskMeta?.name}
                </div>
                {renderTaskDetailFields(parentTaskFields, dropdownMasterValues)}
                {parentTaskDetails.containerNodes.length ? (
                  <Divider style={{ paddingTop: '1px' }} />
                ) : null}
                {renderSubgroups(parentTaskFields, taskResourceTypes.CONTAINERS, dropdownMasterValues, !!parentTaskMeta?.id)}
                {parentTaskDetails.docNodes.length ? (
                  <Divider style={{ paddingTop: '1px' }} />
                ) : null}
                {renderSubgroups(parentTaskFields, taskResourceTypes.AWBS, dropdownMasterValues, !!parentTaskMeta?.id)}
                {parentTaskDetails.awbNodes.length ? (
                  <Divider style={{ paddingTop: '1px' }} />
                ) : null}
                {renderSubgroups(parentTaskFields, taskResourceTypes.DOCUMENTS, dropdownMasterValues, !!parentTaskMeta?.id)}
                {parentTaskDetails.attachments.length ? (
                  <Divider style={{ paddingTop: '1px' }} />
                ) : null}
                {renderAttachments(parentTaskDetails)}
                {renderSeeMore()}
              </>
            )}
          </div>
        )
      : null;
  };

  const renderReason = (reason: string, id: number) => {
    if (reason && reason?.length > 180) {
      return (
        <div>
          {reasonToggleMap[id] ? reason : reason.slice(0, 180)}
          <span
            onClick={() => toggleReasonHeight(id, !reasonToggleMap[id])}
            style={{ color: '#2E6CBD', cursor: 'pointer' }}
          >
            {reasonToggleMap[id] ? ' view less' : ' ... view more'}
          </span>
        </div>
      );
    }
    return <span>{reason}</span>;
  };

  const renderRejectionReason = () => {
    const reversedData = [...taskData?.rejectionReasons].reverse();
    return (
      <div>
        <Collapse
          defaultActiveKey={['1']}
          style={{ marginLeft: '8px', marginRight: '20px', marginTop: '40px' }}
        >
          <Collapse.Panel header="Previous Rejections" key="1">
            <Row gutter={10} style={{ fontSize: '12px', fontWeight: 600 }}>
              <Col span={3}>Sl no.</Col>
              <Col span={17}>Rejection Reason</Col>
              <Col span={4}>Rejection Document</Col>
            </Row>
            <Row gutter={[10, 10]} style={{ marginTop: '20px', fontSize: '12px' }}>
              {reversedData?.map((reason, index) => {
                return (
                  <>
                    <Col
                      span={3}
                      // style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {index + 1}.
                    </Col>
                    <Col span={17} style={{ textAlign: 'justify' }}>
                      {renderReason(reason.rejectionReason, reason.index)}
                    </Col>
                    <Col
                      span={4}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {reason.rejectionDocumentUrl ? (
                        <Tooltip title="Click to Download">
                          <a href={reason.rejectionDocumentUrl} target="_blank">
                            <Icon type="download" style={{ fontSize: '14px' }} />{' '}
                          </a>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </Col>
                    {index === reversedData.length - 1 ? (
                      ''
                    ) : (
                      <>
                        <Col span={24} style={{ marginTop: '10px' }}>{` `}</Col>
                        <Divider dashed style={{ width: '90%', minWidth: '90%', left: '10%' }} />
                      </>
                    )}
                  </>
                );
              })}
            </Row>
          </Collapse.Panel>
        </Collapse>
        {/* Reason of Rejection: {taskData?.rejectionReasons} */}
      </div>
    );
  };

  const renderNoContainers = () => {
    return (
      <div
        style={{
          fontSize: 14,
          fontFamily: 'Open Sans Regular',
          color: '#262626',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 80,
        }}
      >
        <div style={{ marginBottom: 10 }}>
          <CrainIcon />
        </div>
        <span>No tracking numbers added.</span>
        <span>
          Please{' '}
          <span
            style={{
              color: onHold ? 'lightgrey' : '#1890FF',
              pointerEvents: onHold ? 'none' : 'auto',
              cursor: onHold ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
              handleClose();
              gotoContainerTrackingTab();
            }}
          >
            click here
          </span>{' '}
          to add tracking (Container/BL/AWB).
        </span>
      </div>
    );
  };

  const renderIncompleteParentTask = () => {
    return parentTaskMeta?.isEditable ? (
      <div>
        <div>
          Please complete the previous task <b>{parentTaskMeta?.name}</b> in order to fill the
          details for this task.
        </div>
        <div>
          <span
            style={{
              fontWeight: 600,
              color: onHold ? 'lightgrey' : '#1890FF',
              pointerEvents: onHold ? 'none' : 'auto',
              cursor: onHold ? 'not-allowed' : 'pointer',
            }}
            onClick={
              !onHold
                ? () => {
                    form.validateFields(async (err, values) => {
                      onGotoTask(parentTaskMeta?.id, values);
                    });
                  }
                : undefined
            }
          >
            Click here
          </span>{' '}
          to go to {parentTaskMeta?.name}.
        </div>
      </div>
    ) : (
      <div>
        <div>
          The previous task <b>{parentTaskMeta?.name}</b> is not completed.
        </div>
        <div>
          You cannot fill details for this task unless the previous task is completed. Please wait
          until its completion.
        </div>
      </div>
    );
  };

  const renderFailedAPI = () => {
    return (
      <Alert
        type="error"
        className={classes.emptyModal}
        message="Unable to fetch task details. Please refresh!"
      />
    );
  };

  const renderDeleteConfirmationModal = () => {
    return (
      <Modal
        visible={isConfirmationModalVisible}
        onCancel={() => {
          setIsConfirmationModalVisible(false);
          setDocumentToBeDeleted({});
        }}
        confirmLoading={isConfirmationModalLoading}
        onOk={() => {
          setIsConfirmationModalLoading(true);
          handleRemoveDocument();
        }}
        title="Confirmation"
      >
        {'Are you sure you want to delete this document? It will delete the field data also!'}
      </Modal>
    );
  };

  const renderModalContent = () => {
    if (fieldsConfig.status === 'RUNNING') {
      return (
        <Spin
          style={{
            width: '100%',
            marginTop: 100,
            marginBottom: 100,
          }}
        />
      );
    } else {
      return (
        <>
          {renderParentTask()}
          <SEUploadDocumentsTaskDetails
            visible={editable}
            sourceObjectId={sourceObjectId}
            taskId={taskData?.id}
            setApiCallObject={(val) => setDocumentApiCallObject(val)}
            taskUpdateActions={taskUpdateActions}
            taskDetails={taskDetails}
            setIsUpdateDisabled={setIsUpdateDisabled}
            isTemplateMandatory={markDone}
            enableMultipleDuplicateValues={enableMultipleDuplicateValues}
          />
          {renderTaskTabsv2()}
          {taskData?.rejectionReasons?.length ? renderRejectionReason() : ''}
        </>
      );
    }
  };

  return (
    <SEQuoteCandidatesContext.Provider value={quoteCandidatesContextValue}>
      <div className={classes.content}>{renderModalContent()}</div>
      {modals.rejectionRemark.isVisible ? (
        <SETaskRejectionRemarkModal
          isRejectLoading={isRejectLoading}
          taskDetails={taskData}
          visible={modals.rejectionRemark.isVisible}
          handleSubmit={handleRejection}
          handleClose={() => modals.rejectionRemark.hide()}
        />
      ) : null}
      {isConfirmationModalVisible && renderDeleteConfirmationModal()}
      {deviationModal.visible && (
        <SEDeviationReasonModal
          visible={deviationModal.visible}
          totalQuoteAmount={deviationModal.totalQuoteAmount}
          totalDeviation={deviationModal.totalDeviation}
          reasonDropdownMasterId={findQuoteFieldByCode(QUOTE_L1_DEVIATION_REASON_CODE)?.dropdownMasterId}
          dropdownMasterValues={dropdownMasterValues}
          initialValue={deviationInfoRef.current}
          onConfirm={handleDeviationConfirm}
          onCancel={handleDeviationCancel}
        />
      )}
    </SEQuoteCandidatesContext.Provider>
  );
};

const mapStateToProps = ({ masterData }) => ({
  globalCarriers: masterData.globalCarriers,
  enableMultipleDuplicateValues: masterData?.execution?.enableDuplicateInputs,
  enableGridView: masterData?.execution?.enableGridLayout
});

export default connect(mapStateToProps)(
  React.memo(withStyles(styles)(Form.create({})(SEAddTaskDetailModal)))
);
